import React from 'react';
import { Building2, Target, Shield } from 'lucide-react';

const About = () => {
  return (
    <section id="about" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 relative overflow-hidden">
      {/* Background decoration with animated gradients */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full filter blur-3xl animate-float-slow"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-br from-cyan-400/20 to-blue-400/20 rounded-full filter blur-3xl animate-float-slow" style={{ animationDelay: '5s' }}></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-5xl sm:text-6xl font-bold text-blue-900 mb-6">
            About Us
          </h2>
          <div className="w-24 h-1 animated-gradient-border mx-auto mb-8 rounded-full"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Bio-Peptide Ltd is a pioneering leader in bio-peptide research and development, 
            dedicated to creating innovative solutions for health optimization and performance enhancement.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-16">
          {/* Mission Card */}
          <div className="group p-8 bg-gradient-to-br from-white to-blue-50 rounded-3xl border-2 border-blue-100 hover:border-blue-300 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 relative overflow-hidden">
            {/* Animated gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div className="relative z-10">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-700 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To advance human health through cutting-edge bio-peptide technology, 
                making premium-quality peptide solutions accessible to those seeking optimal wellness.
              </p>
            </div>
            
            {/* Bottom glow */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>

          {/* Quality Card */}
          <div className="group p-8 bg-gradient-to-br from-white to-blue-50 rounded-3xl border-2 border-blue-100 hover:border-blue-300 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div className="relative z-10">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-600 to-blue-700 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Quality First</h3>
              <p className="text-gray-600 leading-relaxed">
                Every product undergoes rigorous testing and quality control to ensure 
                the highest standards of purity, potency, and safety for our customers.
              </p>
            </div>
            
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-400 to-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>

          {/* Innovation Card */}
          <div className="group p-8 bg-gradient-to-br from-white to-blue-50 rounded-3xl border-2 border-blue-100 hover:border-blue-300 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div className="relative z-10">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-700 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Innovation</h3>
              <p className="text-gray-600 leading-relaxed">
                Leveraging advanced recombinant DNA technology and pharmaceutical-grade 
                manufacturing processes to deliver breakthrough peptide formulations.
              </p>
            </div>
            
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 to-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>
        </div>

        {/* Company Story with enhanced design */}
        <div className="mt-20 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl blur-xl opacity-30"></div>
          <div className="relative p-12 bg-gradient-to-r from-blue-900 to-blue-800 rounded-3xl text-white shadow-2xl overflow-hidden">
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-grid"></div>
            </div>
            
            <div className="max-w-4xl mx-auto text-center relative z-10">
              <h3 className="text-3xl font-bold mb-6">Our Story</h3>
              <p className="text-lg leading-relaxed opacity-90">
                Founded in Hong Kong, Bio-Peptide Ltd has been at the forefront of peptide innovation 
                for over a decade. Our state-of-the-art facilities and dedicated research team work 
                tirelessly to develop bio-peptide solutions that meet the evolving needs of health-conscious 
                individuals worldwide. From metabolic support to tissue regeneration, our comprehensive 
                product range represents the pinnacle of bio-peptide science.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
